import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { customerProjectBoardsQueryOptions, customerProjectsQueryOptions } from '@/entities/task'
import { ApiError } from '@/shared/api'
import { pluralRu } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { Spinner } from '@/shared/ui/spinner'
import { StatusScreen } from '@/shared/ui/status-screen'

export const Route = createFileRoute('/my/projects_/$projectId')({
  head: () => ({ meta: [{ title: 'Проект · STIT Task' }] }),
  component: CustomerProjectPage,
})

// Доски проекта, открытые заказчику. Закрытая доска сюда не приходит вовсе —
// её не видно и не слышно, как и проекта без единой открытой доски.
function CustomerProjectPage() {
  const { projectId } = Route.useParams()
  const boardsQ = useQuery(customerProjectBoardsQueryOptions(projectId))
  // Имя проекта знает и список проектов (обычно уже в кэше), и любая его доска —
  // берём то, что доехало: пустой проект иначе остался бы безымянным.
  const projectsQ = useQuery(customerProjectsQueryOptions)

  const boards = boardsQ.data ?? []
  const project = projectsQ.data?.find((p) => p.id === projectId)
  const title = project?.name ?? boards[0]?.projectName ?? 'Проект'

  if (boardsQ.isError) {
    // Чужой или закрытый проект отвечает 404 — существование не раскрываем.
    const notFound = boardsQ.error instanceof ApiError && boardsQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Проект недоступен' : 'Не удалось загрузить проект'}
        message={
          notFound
            ? 'Возможно, по нему ещё нечего показывать или он больше не актуален.'
            : 'Попробуйте обновить страницу.'
        }
      >
        {!notFound && (
          <Button
            variant="secondary"
            onClick={() => {
              void boardsQ.refetch()
            }}
          >
            Повторить
          </Button>
        )}
        <Link
          to="/my/projects"
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К моим проектам
        </Link>
      </StatusScreen>
    )
  }

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      <nav aria-label="Хлебные крошки" className="mb-6 text-sm text-text-tertiary">
        <Link to="/my/projects" className="transition-colors hover:text-text-primary">
          Мои проекты
        </Link>
      </nav>

      <PageHeader
        title={title}
        description={project?.description || 'Доски проекта, открытые вашей компании.'}
      />

      {boardsQ.isPending ? (
        <Spinner label="Загрузка досок" />
      ) : boards.length === 0 ? (
        <EmptyState
          title="Досок пока нет"
          description="Здесь появятся доски проекта, как только команда откроет их вам."
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {boards.map((board) => (
            <li key={board.id}>
              <Link
                to="/my/boards/$boardId"
                params={{ boardId: board.id }}
                className="group flex h-full flex-col gap-2 rounded-card bg-bg-surface p-5 ring-1 ring-border-subtle ring-inset transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.35)]"
              >
                <h2 className="text-sm text-text-primary transition-colors group-hover:text-accent">
                  {board.name}
                </h2>
                <div className="mt-auto flex items-center gap-1.5 pt-2 text-xs text-text-tertiary">
                  <Icon name="kanban" size={13} />
                  <span data-numeric className="mono-num">
                    {board.taskCount}
                  </span>
                  {pluralRu(board.taskCount, ['задача', 'задачи', 'задач'])}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
